import React, { useCallback, useEffect, useState } from "react";

import {
  addSchemeToUrlIfEmpty,
  parseUrl,
  removeSchemeFromUrl,
} from "@mapmycustomers/shared/util/url";

import TextField, { TextFieldProps } from "../TextField";

const DEFAULT_SCHEMES = ["https"];

interface UrlFieldProps extends Omit<TextFieldProps, "addonBefore"> {
  /*
   * Allowed schemes for the URL (e.g., ["http", "https", "ftp", "mailto"])
   */
  allowedSchemes?: string[];
  /*
   * The default scheme to use when none is provided in the URL
   */
  defaultScheme?: string;
}

const UrlField = ({
  allowedSchemes = DEFAULT_SCHEMES,
  defaultScheme = "https",
  onChange,
  value,
  ...props
}: UrlFieldProps) => {
  const [scheme, setScheme] = useState(defaultScheme);
  const [inputValue, setInputValue] = useState("");

  const handleChange = useCallback(
    (inputValue: string) => {
      const url = parseUrl(addSchemeToUrlIfEmpty(inputValue, scheme));
      const urlScheme = url?.protocol.slice(0, -1);
      const isHttpUrl = urlScheme === "http";
      const isMailToUrl = urlScheme === "mailto";

      const newInputValue =
        url &&
        urlScheme &&
        ((isHttpUrl && allowedSchemes.includes("https")) || allowedSchemes.includes(urlScheme))
          ? removeSchemeFromUrl(inputValue, urlScheme)
          : inputValue;

      setInputValue(newInputValue);

      // when url is valid use it scheme otherwise use original value
      onChange?.(
        url
          ? `${isHttpUrl ? "https" : urlScheme}:${isMailToUrl ? "" : "//"}${newInputValue}`
          : inputValue
      );
    },
    [allowedSchemes, onChange, scheme]
  );

  useEffect(() => {
    const url = value ? parseUrl(addSchemeToUrlIfEmpty(value, defaultScheme)) : undefined;
    const urlScheme = url?.protocol?.slice(0, -1);

    if (urlScheme && allowedSchemes.includes(urlScheme)) {
      setScheme(urlScheme);
    }

    // use original value when url is not valid
    // value is using instead of url to preserve the original input value and void encoding changes
    if (value !== undefined) {
      setInputValue(url ? removeSchemeFromUrl(value, urlScheme) : value);
    }
  }, [value, defaultScheme, allowedSchemes]);

  return (
    <TextField
      {...props}
      addonBefore={`${scheme}:${scheme === "mailto" ? "" : "//"}`}
      onChange={handleChange}
      value={inputValue}
    />
  );
};

export default UrlField;
